/* eslint-disable max-len */
/* eslint-disable react/prop-types */
/* eslint-disable object-curly-newline */
import React, { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useHistory, useLocation } from 'react-router';
import { sendRequest } from '../../../api/shootAPI';
import API from '../../../api/endpoints';
import { UserContext } from '../../../contexts/User';
import DataTable from '../../../components/DataTable';
import StatusPill from '../../../components/StatusPill';
import AlertFilterMenu from './AlertFilterMenu';

const PAGE_SIZE = 10;
const dash = <span className="text-ink-500">—</span>;
const PERF_TONE = { Low: 'error', Average: 'warning', High: 'success' };

const Performance = [
  { id: 0, name: 'All' }, { id: 1, name: 'Low' }, { id: 2, name: 'Average' }, { id: 3, name: 'High' },
];

function TeamPerformance() {
  const { search } = useLocation();
  const { activeWorkspace } = useContext(UserContext);
  const query = new URLSearchParams(search);
  const page = query.get('page');
  const history = useHistory();

  const [isLoading, setIsLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [reports, setReports] = useState([]);
  const [currentPage, setCurrentPage] = useState(page ? parseInt(page, 10) : 1);
  const [teams, setTeams] = useState([{ id: 0, name: 'All' }]);
  const [managers, setManagers] = useState([{ id: 0, name: 'All' }]);
  const [teamId, setTeamId] = useState(0);
  const [managerId, setManagerId] = useState(0);
  const [performanceId, setPerformanceId] = useState(0);

  const handlePageChange = (next) => { setCurrentPage(next); history.push(`?page=${next}`); };

  const getTeamPerformance = async (params) => {
    setIsLoading(true);
    const response = await sendRequest({
      method: 'get',
      url: API.WORKSPACE.getTeamPerformance(activeWorkspace.id),
      params: { pageSize: PAGE_SIZE, ...params },
    });
    if (response.success) {
      const mapped = response.data?.performanceReports.map((r) => {
        const totalSecondsWorked = r?.totalSecondsWorked ?? 0;
        const totalActiveSeconds = r?.totalActiveSeconds ?? 0;
        const rate = totalActiveSeconds === 0 ? 0 : Math.round((totalActiveSeconds * 100) / totalSecondsWorked);
        const performance = rate < 21 ? 'Low' : rate < 51 ? 'Average' : 'High'; // eslint-disable-line no-nested-ternary
        return {
          id: r?.teamId,
          teamId: r?.teamId,
          teamName: r?.teamName,
          managerName: r?.managerName,
          hoursWorked: Math.round(totalSecondsWorked / 3600),
          idleTime: Math.round((totalSecondsWorked - totalActiveSeconds) / 3600),
          expectedHours: (r?.teamMemberCount ?? 0) * 8,
          performance,
        };
      });
      setReports(mapped);
      setTotalPages(response.data?.totalPages ?? 1);
    }
    setIsLoading(false);
  };

  const getAllTeams = async () => {
    const response = await sendRequest({ method: 'get', url: API.WORKSPACE.getAllTeamInfo(activeWorkspace.id) });
    if (response.success) {
      const mappedTeams = response.data?.teams.map((t) => ({ id: t?.id, name: t.name }));
      const mappedManagers = response.data?.teams.map((t) => ({ id: t.teamMates[0].member?.id, name: t.teamMates[0].member?.user?.fullName }));
      const seen = {};
      const dedupManagers = mappedManagers.filter((i) => {
        if (seen[i.name]) return false;
        seen[i.name] = true;
        return true;
      });
      setTeams([{ id: 0, name: 'All' }, ...mappedTeams]);
      setManagers([{ id: 0, name: 'All' }, ...dedupManagers]);
    }
  };

  const handleReset = () => {
    setTeamId(0);
    setManagerId(0);
    setPerformanceId(0);
  };

  const handleSearch = () => {
    const perf = Performance.find((p) => p.id === Number(performanceId));
    getTeamPerformance({
      teamIds: Number(teamId) === 0 ? null : [Number(teamId)],
      managers: Number(managerId) === 0 ? null : [Number(managerId)],
      performances: !perf || perf.id === 0 ? null : [perf.name],
      page: currentPage,
    });
  };

  useEffect(() => { getTeamPerformance({ page: currentPage }); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [currentPage]);
  useEffect(() => { getAllTeams(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const columns = [
    { key: 'teamName', label: 'Team Name', width: 200, flex: true, render: (r) => <span className="font-medium text-ink-900 truncate block" title={r.teamName}>{r.teamName || dash}</span> },
    { key: 'managerName', label: 'Manager', width: 180, flex: true, render: (r) => <span className="text-ink-700 truncate block">{r.managerName || dash}</span> },
    { key: 'hoursWorked', label: 'Hours Worked', width: 130, render: (r) => <span className="tabular-nums text-ink-900">{`${r.hoursWorked}h`}</span> },
    { key: 'idleTime', label: 'Idle Time', width: 110, render: (r) => <span className="tabular-nums text-ink-700">{`${r.idleTime}h`}</span> },
    { key: 'expectedHours', label: 'Expected', width: 110, render: (r) => <span className="tabular-nums text-ink-700">{`${r.expectedHours}h`}</span> },
    { key: 'performance', label: 'Performance', width: 130, render: (r) => <StatusPill tone={PERF_TONE[r.performance] || 'neutral'}>{r.performance}</StatusPill> },
    {
      key: 'actions',
      label: 'Action',
      width: 180,
      align: 'center',
      render: (r) => (
        <Link to={`/members/team-engagement/${r.teamId}`} onClick={(e) => e.stopPropagation()}>
          <button type="button" className="inline-flex items-center justify-center px-3 py-1.5 rounded-[8px] text-[11px] font-semibold uppercase tracking-wider text-ink-900 bg-white border border-rule hover:border-brand-500 hover:text-brand-700 transition whitespace-nowrap" style={{ borderStyle: 'solid', letterSpacing: '0.05em' }}>
            View Team Engagement
          </button>
        </Link>
      ),
    },
  ];

  return (
    <>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
        <div className="min-w-0">
          <h4 className="text-base font-semibold text-ink-900 mb-0.5">Team Performance</h4>
          <p className="text-sm text-ink-500">Review every team&apos;s performance along with their engagement details</p>
        </div>
        <AlertFilterMenu
          onReset={handleReset}
          onSearch={handleSearch}
          groups={[
            { label: 'Team Name', options: teams, value: teamId, onChange: setTeamId },
            { label: 'Manager Name', options: managers, value: managerId, onChange: setManagerId },
            { label: 'Performance', options: Performance, value: performanceId, onChange: setPerformanceId },
          ]}
        />
      </div>
      <DataTable
        columns={columns}
        rows={reports}
        getRowKey={(r) => r.id}
        loading={isLoading}
        emptyText="There are no data yet."
        page={currentPage}
        pageSize={PAGE_SIZE}
        total={totalPages * PAGE_SIZE}
        onPageChange={handlePageChange}
        minWidth={1040}
      />
    </>
  );
}

export default TeamPerformance;
